import { type SimpleGit, simpleGit } from "simple-git";
import type { CommitInfo } from "../types.js";

export class GitClient {
    private git: SimpleGit;
    private repoPath: string;

    constructor(repoPath: string) {
        this.repoPath = repoPath;
        this.git = simpleGit(repoPath);
    }

    async exec(args: string[]): Promise<string> {
        return this.git.raw(args);
    }

    async execWithInput(args: string[], input: string): Promise<string> {
        // simple-git's raw() doesn't support stdin directly,
        // so we use spawn for commands that need stdin (e.g. git am)
        const { spawn } = await import("node:child_process");

        return new Promise((resolve, reject) => {
            const proc = spawn("git", args, { cwd: this.repoPath });
            let stdout = "";
            let stderr = "";

            proc.stdout.on("data", (data: Buffer) => {
                stdout += data.toString();
            });
            proc.stderr.on("data", (data: Buffer) => {
                stderr += data.toString();
            });

            proc.on("error", (err) => {
                reject(new Error(`git ${args.join(" ")} failed to spawn: ${err.message}`));
            });

            // git may exit (success or failure) before consuming all of stdin
            // (e.g. `git apply` rejecting a large patch early). The resulting
            // EPIPE on the stdin socket must be swallowed — the outcome is
            // decided by the exit code in the "close" handler.
            proc.stdin.on("error", () => {});

            proc.on("close", (code) => {
                if (code === 0) {
                    resolve(stdout);
                } else {
                    reject(new Error(`git ${args.join(" ")} failed (code ${code}): ${stderr}`));
                }
            });

            proc.stdin.write(input);
            proc.stdin.end();
        });
    }

    async formatPatch(commitSha: string): Promise<string> {
        return this.exec(["format-patch", "-1", commitSha, "--stdout"]);
    }

    async applyPatch(patchContent: string): Promise<void> {
        await this.execWithInput(["am", "--3way"], patchContent);
    }

    async getTreeHash(commitSha: string): Promise<string> {
        return (await this.exec(["rev-parse", `${commitSha}^{tree}`])).trim();
    }

    async showFile(treeish: string, filePath: string): Promise<string | null> {
        try {
            return await this.exec(["show", `${treeish}:${filePath}`]);
        } catch {
            return null;
        }
    }

    async getCommitInfo(commitSha: string): Promise<CommitInfo> {
        const format = "%H%x00%an%x00%ae%x00%s";
        const output = await this.exec(["log", "-1", `--format=${format}`, commitSha]);
        const [sha, authorName, authorEmail, message] = output.trim().split("\0");
        return { sha, authorName, authorEmail, message };
    }

    async getCommitParents(commitSha: string): Promise<string[]> {
        const output = await this.exec(["rev-parse", `${commitSha}^@`]);
        return output.trim().split("\n").filter(Boolean);
    }

    /**
     * Detects renames and copies between two trees.
     *
     * Both `R` (rename) and `C` (copy) entries are returned as `{from, to}` pairs.
     * Copies are treated as rename-equivalent because the generator may produce the
     * new path while the old path remains in the tree (e.g., imperfect cleanup,
     * customer edits at the old path, or test helpers that only write new files).
     * Callers that care about "is the source still present" should verify via
     * `showFile(toTree, from)`.
     */
    async detectRenames(fromTree: string, toTree: string): Promise<Array<{ from: string; to: string }>> {
        try {
            // --find-copies (-C) catches the case where the generator writes the new path
            // but leaves the old path in the tree (e.g., when the generator's cleanup step
            // didn't run, or customer edits at the old path kept it present). Rename
            // detection alone requires the old path to be deleted; copy detection does not.
            const output = await this.exec([
                "diff", "--find-renames", "--find-copies", "--name-status", fromTree, toTree
            ]);
            const renames: Array<{ from: string; to: string }> = [];
            for (const line of output.trim().split("\n")) {
                if (!line) continue;
                // Lines look like: R095\told/path.ts\tnew/path.ts  (rename, 95% similarity)
                //              or: C095\tsrc/path.ts\tnew/path.ts  (copy,   95% similarity)
                if (line.startsWith("R") || line.startsWith("C")) {
                    const parts = line.split("\t");
                    if (parts.length >= 3) {
                        renames.push({ from: parts[1]!, to: parts[2]! });
                    }
                }
            }
            return renames;
        } catch {
            return [];
        }
    }

    async isAncestor(commit: string, descendant: string): Promise<boolean> {
        try {
            const mergeBase = (await this.exec(["merge-base", commit, descendant])).trim();
            return mergeBase === commit;
        } catch {
            // No common ancestor (e.g., orphan branches)
            return false;
        }
    }

    async commitExists(sha: string): Promise<boolean> {
        try {
            const type = await this.exec(["cat-file", "-t", sha]);
            return type.trim() === "commit";
        } catch {
            return false;
        }
    }

    async treeExists(treeHash: string): Promise<boolean> {
        try {
            const type = await this.exec(["cat-file", "-t", treeHash]);
            return type.trim() === "tree";
        } catch {
            return false;
        }
    }

    async getCommitBody(commitSha: string): Promise<string> {
        return this.exec(["log", "-1", "--format=%B", commitSha]);
    }

    getRepoPath(): string {
        return this.repoPath;
    }
}
